import styled from '@emotion/styled';
import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import type { BarProps } from './Bar';
import { Bar } from './Bar';

export interface VerticalBarChartProps {
  data?: BarProps[];
  className?: string;
  absoluteValues?: boolean;
  maxValue?: number;
  testId?: string;
}

export const getPercent = (value: number, max: number) => {
  return value > 0 ? (100 * value) / max : 0;
};

const Component = ({
  data = [],
  className,
  absoluteValues,
  maxValue: _maxValue = 0,
  testId,
}: VerticalBarChartProps) => {
  const maxValue = useMemo(
    () => _maxValue || Math.max(0, ...data.map((item) => item.value)),
    [_maxValue, data],
  );

  const [hidden, setHidden] = useState(true);

  const [ref, inView] = useInView({
    threshold: 0.33,
  });

  useEffect(() => {
    if (inView) setHidden(false);
  }, [inView]);

  return (
    <Root className={className} data-test-id={testId} ref={ref}>
      {data?.map((item, index) => (
        <Bar
          key={index}
          {...item}
          value={hidden ? 0 : absoluteValues ? item.value : getPercent(item.value, maxValue)}
          color={item.color}
        />
      ))}
    </Root>
  );
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const VerticalBarChart = Object.assign(Component, {
  Root,
  Bar: {
    Root: Bar.Root,
    Title: Bar.Title,
    Line: Bar.Line,
    Progress: Bar.Progress,
    Label: Bar.Label,
  },
});
